import * as React from 'react';
import { LoadingIndicator, PVATeamSelect, PVABadge, PVAContentHeader, PVAContentBody, LoadingIndicatorMini, PVAMultiSelectLight, PVAStyleModal } from '../../../components';
import { ReportType } from '../../../enums';
import {Auth, ReportDataService, StyleDataService, TeamologyDataService} from '../../../services';
import { User, UserLight, Style, Team, ReportTeamologyConfig } from '../../../models';
import { BaseRouteProps } from '../../../interfaces';
import { computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { UserStore, FilterStore } from '../../../stores';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { PonderaModal } from '../../inclinationmodals';
import './TeamologyReport.css';

class TeamologyReportData {
    styles: Style[];
    users: UserLight[];
    generalECAverage: number;
    satisfactionAverage: number;
}

interface TeamologyReportState {
    orgId: number;
    surveyVersionId: number;
    selectedUserData: any;
    reportType: ReportType;
    reportData: TeamologyReportData | undefined;
    loading: boolean;
    teamId: number;
    mode: number;
    filter: string;
    miniLoad: boolean;
    selectedUserIds: number[];
    showClarity: boolean;
    config: ReportTeamologyConfig | undefined;
    clarityModal: boolean;
    exporting: boolean;
}

interface TeamologyReportProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

@inject('UserStore', 'FilterStore') @observer
export class TeamologyReport extends React.Component<TeamologyReportProps, TeamologyReportState> {
    private reportDataService = new ReportDataService(this.props.notificationService);
    private styleDataService = new StyleDataService(this.props.notificationService);
    private teamologyDataService = new TeamologyDataService(this.props.notificationService);

    constructor(props: TeamologyReportProps) {
        super(props);

        this.state = {
            orgId: 0,
            surveyVersionId: 0,
            selectedUserData: null,
            reportData: undefined,
            reportType: ReportType.Standard,
            loading: false,
            teamId: 0,
            mode: 0,
            filter: '',
            miniLoad: false,
            selectedUserIds: [],
            showClarity: false,
            config: undefined,
            clarityModal: false,
            exporting: false,
        };
    }

    @computed get team() {
        if (this.props.FilterStore && this.props.FilterStore.team) {
            return this.props.FilterStore.team;
        }
        return null;
    }

    @computed get teamsThisUserManages() {
        const auth = new Auth();
        const managedTeams = auth.managedTeams();
        if (this.props.FilterStore && this.props.FilterStore.teams) {
            return this.props.FilterStore.teams.filter(t => managedTeams.includes(t.id));
        }
        return [];
    }

    @computed get canAccessReportData() {
        return this.userRole !== 'user' || this.teamsThisUserManages.length > 0;
    }

    @computed get teams() {
        if (this.props.FilterStore && this.props.FilterStore.teams && this.props.FilterStore.teams.length > 0) {
            return this.props.FilterStore.teams;
        }
        return this.teamsThisUserManages;
    }

    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get users() {
        if (this.props.FilterStore && this.props.FilterStore.users) {
            return this.props.FilterStore.users;
        }
        return [];
    }

    @computed get survey() {
        if (this.props.FilterStore && this.props.FilterStore.survey) {
            return this.props.FilterStore.survey;
        }
        return null;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get teamId() {
        return this.props.FilterStore!.teamId;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    public update() {
        if (this.canAccessReportData) {
            if (this.organization && this.organization.id !== this.state.orgId) {
                this.setState(
                    {
                        orgId: this.organization.id,
                        teamId: 0,
                        selectedUserIds: [],
                        selectedUserData: null
                    },
                    () => {
                        if (this.organization) {
                            this.teamologyDataService.getConfig(this.organization).then((settings: ReportTeamologyConfig) => {
                                this.setState({
                                    config: settings
                                });
                            });
                        }
                    });

            }

            if (this.surveyVersion && this.surveyVersion.id !== this.state.surveyVersionId) {
                this.setState({
                    surveyVersionId: this.surveyVersion.id
                });

                this.styleDataService.getAllBySurveyVersion(this.surveyVersion).then((styles: Style[]) => {
                    this.setState({
                        reportData: {
                            styles: styles,
                            users: [],
                            generalECAverage: 0,
                            satisfactionAverage: 0
                        }
                    });
                });
            }
            
            let teamId = this.teamId;
            if (this.team) {
                teamId = this.team.id;
            }

            if (this.surveyVersion && this.survey && this.state.teamId !== teamId) {
                const surveyId = this.survey.id;
                
                this.setState({
                    miniLoad: true
                });
    
                if (this.team) {
                    this.reportDataService.getTeamologyTeamReportData(this.team, surveyId, this.surveyVersion.id).then((reportData: TeamologyReportData) => {
                        this.setState({
                            reportData: reportData,
                            miniLoad: false,
                            teamId: teamId
                        });
                    });
                } else if (this.organization && teamId === -100) {
                    this.reportDataService.getTeamologyOrganizationReportData(this.organization, surveyId, this.surveyVersion.id).then((reportData: TeamologyReportData) => {
                        this.setState({
                            reportData: reportData,
                            teamId: teamId,
                            miniLoad: false
                        });
                    });
                } else if (this.organization && teamId === -200) {
                    this.reportDataService.getTeamologyCandidateReportData(this.organization, surveyId, this.surveyVersion.id).then((reportData: TeamologyReportData) => {
                        this.setState({
                            reportData: reportData,
                            teamId: teamId,
                            miniLoad: false
                        });
                    });
                } else if (this.organization && teamId === -300) {
                    this.reportDataService.getTeamologyFormerEmployeeReportData(this.organization, surveyId, this.surveyVersion.id).then((reportData: TeamologyReportData) => {
                        this.setState({
                            reportData: reportData,
                            teamId: teamId,
                            miniLoad: false
                        });
                    });
                } else {
                    if (this.surveyVersion) {
                        this.styleDataService.getAllBySurveyVersion(this.surveyVersion).then((styles: Style[]) => {
                            this.setState({
                                reportData: {
                                    styles: styles,
                                    users: [],
                                    generalECAverage: 0,
                                    satisfactionAverage: 0,
                                    mbtiScore: 0
                                },
                                teamId: teamId,
                                miniLoad: false
                            });
                        });
                    } else {                    
                        this.setState({
                            miniLoad: false
                        });
                    }
                }
            }
        }
    }

    componentDidMount() {
        this.update();
    }

    componentWillReact() {
        this.update();
    }

    public submit(values: any) {
        this.reportDataService.getIndividualReportData(values.userId).then((reportData) => {
            this.setState({
                selectedUserData: reportData,
                reportType: values.reportType
            });
        });
    }

    toggleModal(name: string) {
        if (!this.state.reportData) {
            return;
        }

        this.state.reportData.styles.map((style: Style) => {
            if (style && style.name.toLowerCase() === name) {
                style.showModal = !style.showModal;
            }
        });
        this.forceUpdate();
    }

    toggleClarityModal() {
        this.setState({
            clarityModal: !this.state.clarityModal
        });
        this.forceUpdate();
    }

    userSort(a: any, b: any) {
        if (a.user.lastName > b.user.lastName) {
            return 1;
        } else if (a.user.lastName < b.user.lastName) {
            return -1;
        }
        if (a.user.firstName > b.user.firstName) {
            return 1;
        } else if (a.user.firstName < b.user.firstName) {
            return -1;
        }
        return 0;
    }

    styleMatch(user: UserLight) {
        if (!this.state.reportData) {
            return null;
        }

        var style = this.state.reportData.styles.find((s: any) => {
            return s.id === user.styleId;
        });
        if (style) {
            return style;
        }

        return this.state.reportData.styles.find((s: any) => {
            return (user.styleName === s.name && user.styleCore === s.core);
        });
    }

    teamChange(e: any) {
        if (this.props.FilterStore) {
            var id = Number(e.target.value);
            var team = this.teams.find((t: Team) => {
                return t.id === id;
            });
            if (team) {
                this.props.FilterStore.setTeam(team);
            } else {
                // -100, -200, -300, etc
                this.props.FilterStore.setTeamID(id);
            }
            // this.props.FilterStore.teamId = Number(e.target.value);
            this.setState({
                selectedUserIds: []
            });
        }
    }

    toggleUserSelect(e: any) {
        var id = Number(e.target.value);
        if (id === -1) {
            // select all users
            var tempids: number[] = [];
            this.users.map((u: User) => {
                if (!u.resultsArePrivate) {
                    tempids.push(u.id);
                }
            });
            this.setState({
                selectedUserIds: tempids
            });
            return;
        }
        if (id === -2) {
            // deselect all users
            this.setState({
                selectedUserIds: []
            });
            return;
        }

        var ids = this.state.selectedUserIds;
        var ind = ids.indexOf(id);
        if (ind > -1) {
            ids.splice(ind, 1);
        } else {
            ids.push(id);
        }
        this.setState({
            selectedUserIds: ids
        });
    }

    private GetKeyOverride(key?: string, fallback?: any) {
        if (key && this.props.FilterStore) {
            return this.props.FilterStore.GetKeyOverride(key, fallback || '');
        }
        return fallback;
    }

    private getClarityClass(user: any) {
        if (user.mbtiScore < 32) {
            return 'clarity clarity-low';
        } else if (user.mbtiScore < 61) {
            return 'clarity clarity-mid';
        } else if (user.mbtiScore < 100) {
            return 'clarity clarity-high';
        }
        return '';
    }

    private abbr(name: string) {
        var words = name.split(' ');
        var result = '';
        words.map((w: string) => {
            result += w[0];
        });
        return result;
    }

    private getCoreShort(style: Style, styleConnectedId: number) {
        if (this.state.reportData && style.useSecondaryStyle) {
            var subStyle = this.state.reportData.styles.find(x => x.id === styleConnectedId);
            if (subStyle != null) {
                return subStyle.name;
            }
        }
        return style.subCore ? this.abbr(style.subCore.name) : style.core;
    }
    private getCoreFull(style: Style, styleConnectedId: number) {
        if (this.state.reportData && style.useSecondaryStyle) {
            var subStyle = this.state.reportData.styles.find(x => x.id === styleConnectedId);
            if (subStyle != null) {
                return subStyle.name;
            }
        }
        return style.subCore ? style.subCore.name : style.core;
    }

    public render() {

        var styleUsers: any = {};
        var styleUserEntries: any = [];
        var styleMap: Style[] = [];
        var userCount = 0;

        if (this.state.reportData) {
            this.state.reportData.styles.map((style: Style) => {
                var styleName = style.name.toLowerCase();
                if (styleUsers[styleName] === undefined) {
                    styleUsers[styleName] = {
                        name: style.name,
                        modalVisible: style.showModal,
                        content: '',
                        tooltip: '',
                        users: []
                    };
                    if (style.styleTexts) {
                        var stBrief = style.styleTexts.find((x: any) => {
                            return x.styleTextType === 'brief';
                        });
                        var stContent = style.styleTexts.find((x: any) => {
                            return x.styleTextType === 'content';
                        });
                        if (stBrief) {
                            styleUsers[styleName].tooltip = stBrief.value;
                        }
                        if (stContent) {
                            styleUsers[styleName].content = stContent.value;
                        }
                    }
                    styleUserEntries.push(styleName);
                    styleMap.push(style);
                }

                if (this.state.reportData) {
                    this.state.reportData.users.filter((u: UserLight) => {
                        if (this.state.selectedUserIds.indexOf(u.id) === -1) {
                            return false;
                        }
                        if (!this.state.filter || this.state.filter === '') {
                            return true;
                        }
                        return u.firstName.toLowerCase().indexOf(this.state.filter.toLowerCase()) > -1 || u.lastName.toLowerCase().indexOf(this.state.filter.toLowerCase()) > -1;
                    }).map((user: UserLight) => {

                        var userStyle = this.styleMatch(user);
                        if (userStyle) {

                            if (this.state.mode === 0 && (userStyle.id === style.id))  {
                                styleUsers[styleName].users.push({
                                    user: user,
                                    core: this.getCoreShort(style, style.secondaryStyleID),
                                    coreFull: this.getCoreFull(style, style.secondaryStyleID)
                                });
                            } else if (this.state.mode === 1 && userStyle.id === style.secondaryStyleID) {
                                styleUsers[styleName].users.push({
                                    user: user,
                                    core: this.getCoreShort(style, userStyle.id),
                                    coreFull: this.getCoreFull(style, userStyle.id)
                                });
                            } else if (this.state.mode === 2 && userStyle.id === style.tertiaryStyleID) {
                                styleUsers[styleName].users.push({
                                    user: user,
                                    core: this.getCoreShort(style, userStyle.id),
                                    coreFull: this.getCoreFull(style, userStyle.id)
                                });
                            } else if (this.state.mode === 3 && userStyle.id === style.leastPreferredStyleID) {
                                styleUsers[styleName].users.push({
                                    user: user,
                                    core: this.getCoreShort(style, userStyle.id),
                                    coreFull: this.getCoreFull(style, userStyle.id)
                                });
                            }
                        }
                    });
                } else {
                    console.log('no report data');
                }
            });
        }

        var resultingRows: any[] = [];
        for (var i = 0; i < styleUserEntries.length; i += 4) {
            var keys = [i + 0, i + 1, i + 2, i + 3];

            var row = (
                <div className="row" key={i}>
                    {
                        keys.map((ind: number) => {                            
                            var dataName = styleUserEntries[ind];
                            var style = styleMap[ind];
                            var data = styleUsers[dataName];
                            if (!data) {
                                return <div key={`header-${dataName}`} />;
                            }
                            const modalProps = {
                                show: data.modalVisible,
                                onHide: this.toggleModal.bind(this, dataName),
                                title: data.displayName ? data.displayName : data.name,
                                content: data.content,
                                style: style,
                                name: ' '
                            };
                            
                            const tooltip = <Tooltip><span dangerouslySetInnerHTML={{ __html: data.tooltip }} /></Tooltip>;
                            
                            return <div key={`header-${dataName}`} className="col-xs-12 col-sm-6 col-md-3">
                                <OverlayTrigger animation={''} delay={0} placement="top" overlay={tooltip}>
                                    <a className="teamology-header pva-badge-small" onClick={(e) => { this.toggleModal(dataName); }}>
                                        <PVABadge badge={data.name[0]} style={data.name} />
                                        <span>{data.name}</span>
                                    </a>
                                </OverlayTrigger>
                                <div className="teamology-body">
                                    {
                                        data.users.sort(this.userSort).map((d: any) => {
                                            const tooltipSub = <Tooltip className="normal"><div className="text-center">{d.user.lastName}, {d.user.firstName} ({d.coreFull})</div></Tooltip>;
                                            userCount++;
                                            return <OverlayTrigger key={d.user.id} animation={''} delay={0} placement="top" overlay={tooltipSub}>
                                                    <div className={this.getClarityClass(d.user)}>
                                                        <span>{d.user.lastName}, {d.user.firstName} </span>
                                                        <small><i>({d.core})</i></small>
                                                    </div>
                                                </OverlayTrigger>;
                                        })
                                    }
                                    <PVAStyleModal styleDataService={this.styleDataService} notificationService={this.props.notificationService} {...modalProps} />
                                </div>
                            </div>;
                        })
                    }
                </div>);

            resultingRows.push(row);
        }

        let defaultOptions: any[] = [{
            id: 0,
            value: '- Select -'
        }];
        
        if (!this.props.UserStore!.userRoleIn(['manager', 'user']) || this.userPermissions.indexOf('viewAllUsers') > -1 ) {
            defaultOptions.push({
                id: -100,
                value: 'All Users'
            });
        }
        if (!this.props.UserStore!.userRoleIn(['manager', 'user']) || this.userPermissions.indexOf('viewAllCandidates') > -1 ) {
            defaultOptions.push({
                id: -200,
                value: 'All Candidates'
            });
        }
        if (!this.props.UserStore!.userRoleIn(['manager', 'user']) || this.userPermissions.indexOf('viewAllFormerEmployees') > -1 ) {
            defaultOptions.push({
                id: -300,
                value: 'All Former Employees'
            });
        }

        var defaultUserOptions: any[] = [{
            id: -2,
            value: 'No Individuals'
        }, {
            id: -1,
            value: 'All Individuals'
        }];

        const getStylePercentagesForSelectedUsers = () => {
            const styleCounts:any[] = [];
            let total = 0;
            styleUserEntries.forEach((e:any) => {
                let count = styleUsers[e].users.length;
                count = count == null ? 0 : count;
                total += count;
                styleCounts.push({style:e,count:count,percent:0});
            });
            if (total === 0) {
                return styleCounts;
            }
            for(let x = 0; x < styleCounts.length; x++) {
                styleCounts[x].percent = Math.floor((styleCounts[x].count*1.0 / total)*100);
                styleCounts[x].total = total;
            }
            return styleCounts;
        };
        const getStylePercentagesPresentation = () => {
            let total = 0;
            let totalPercent = "0%";
            const styles = getStylePercentagesForSelectedUsers().map((p:any) => {
                total += p.count;
                return (
                    <div className="row" key={"percent-container-" + p.style}>
                        <div className="col-xs-3 col-md-3 col-lg-3">
                            <div key={"percent-breakdown-" + p.style} style={{textTransform:"capitalize"}}>{p.style + "s: "}</div>
                        </div>
                        <div className="col-xs-3 col-md-2 col-lg-1" style={{textAlign:"right"}}>
                            {p.count}
                        </div>
                        <div className="col-xs-3 col-md-2 col-lg-1" style={{textAlign:"right"}}>
                            {p.percent + "%"}
                        </div>
                    </div>
                );
            });
            if (total > 0) {
                totalPercent = "100%";
            }
            styles.push((
                <div
                    className="row"
                    key={"percent-container-total-border"}
                >
                    <div
                        className="col-xs-9 col-md-7 col-lg-5"
                        style={{borderTop:"grey 1px solid", marginTop:"1em", paddingTop:"1em"}}
                    />
                </div>
            ));
            styles.push((
                <div
                    className="row"
                    key={"percent-container-total"}
                >
                    <div className="col-xs-3 col-md-3 col-lg-3">
                        TOTAL:
                    </div>
                    <div className="col-xs-3 col-md-2 col-lg-1" style={{textAlign:"right"}}>
                        {total}
                    </div>
                    <div className="col-xs-3 col-md-2 col-lg-1" style={{textAlign:"right"}}>
                        {totalPercent}
                    </div>
                </div>
            ));
            return styles;
        };

        var clarityContent = (
            <div>
                <div>The Clarity Index highlights how consistently the individual completing the instrument responded to the statements on a number of scales compared to the maximum possible index that would be achievable by those scales by answering all the items associated with those scales.</div>
                <br />
                <div>Therefore, the more elevated the index, it can be assumed that the individual is more clear about their preference for a particular style. Lower scores on the clarity index (shown in GREEN) only suggest that the individual is less clear about their preference for a particular style. The Clarity Index does not measure skills or ability or degree of use for a specific style, only clarity of preference.</div>
            </div>
        );
        const exportPdf = async (e: any) => {
            this.props.notificationService.addNotification("PDF export started");
            this.setState({
                exporting: true
            });
            e.preventDefault();
            try {
                await this.reportDataService.getTeamologyPDF(this.state.surveyVersionId, this.teamId);
                this.props.notificationService.addNotification("PDF export complete");
            }
            catch (e) {
                this.props.notificationService.addError(`Failed to export PDF. ${e.toString()}`);
            }
            finally {
                this.setState({
                    exporting: false
                });
            }
        }

        return (
            <div className={this.state.showClarity ? 'show-clarity' : ''}>
                {this.team && <div id={`team-${this.team.id}`} />}
                {this.organization && <div id={`organization-${this.organization.id}`} />}
                {this.survey && <div id={`survey-${this.survey.id}`} />}

                <PVAContentHeader sticky="sticky">
                    <div className="row">
                        <div className="col-xs-8">
                            <div className="title">
                                {this.GetKeyOverride('teamologyReport', <span>PVA Teamology<span className="trademark">TM</span></span>)}
                                <span className="light"> for </span>
                                <PVATeamSelect defaultOptions={defaultOptions} options={this.teams} selected={this.teamId} onChange={(e: any) => { this.teamChange(e); }} />
                                {this.state.reportData && <PVAMultiSelectLight defaultOptions={defaultUserOptions} options={this.state.reportData.users}  selected={this.state.selectedUserIds} onChange={(e: any) => this.toggleUserSelect(e)} FilterStore={this.props.FilterStore} />}
                                {this.state.miniLoad && <LoadingIndicatorMini />}                        
                            </div>
                        </div>
                        <div className="col-xs-4 row">
                            <button className="btn btn-default col-xs-5" onClick={(e) => this.setState({ showClarity: !this.state.showClarity })}>
                                {this.state.showClarity ? <span>Hide Clarity</span> : <span>Show Clarity</span>}
                            </button>
                            <button className="btn btn-default col-xs-5" disabled={this.state.exporting || this.teamId <= 0} onClick={exportPdf}>Export PDF</button>
                        </div>
                    </div>
                </PVAContentHeader>

                <PVAContentBody>
                    <div className="row">
                        <div className="col-sm-3">
                            <label>Cognitive Focus</label>
                            <select className="form-control" value={this.state.mode} onChange={(e) => { this.setState({mode: Number(e.target.value)}); }}>
                                <option value={0}>Primary Style</option>
                                <option value={1}>Secondary Style</option>
                                {this.userPermissions.indexOf('teamologyTertiaryAccess') > -1 && <option value={2}>Tertiary Style</option>}
                                <option value={3}>Least Preferred Style</option>
                            </select>
                        </div>
                        <div className="col-sm-3">
                            <label>Filter</label>
                            <input className="form-control" type="text" placeholder="Filter Name" value={this.state.filter} onChange={(e) => this.setState({filter: e.target.value})} />
                        </div>
                    </div>
                    <br />
                    {this.state.loading && <LoadingIndicator />}
                    {!this.state.loading && <div>
                        <div className="row">
                            <div className="col-sm-6 text-center teamology-header-indicator">
                                {this.getConfigText(0)}
                            </div>
                            <div className="col-sm-6 text-center teamology-header-indicator">
                                {this.getConfigText(1)}
                            </div>
                        </div>
                        {resultingRows}                        
                        <div className="row">
                            <div className="col-sm-6 text-center teamology-header-indicator">
                                {this.getConfigText(2)}
                            </div>
                            <div className="col-sm-6 text-center teamology-header-indicator">
                                {this.getConfigText(3)}
                            </div>
                        </div>
                        <br /><br />
                        <div className="row">
                            <div className="col-xs-6">
                                <div className="row">
                                    <div className="col">
                                        {this.state.reportData && <div>
                                            Average General Emotional Component: {Math.round(this.state.reportData.generalECAverage)}%
                                        </div>}
                                        {this.state.reportData && <div>
                                            Average Team Statisfaction: {Math.round(this.state.reportData.satisfactionAverage)}%
                                        </div>}
                                        <div>
                                            Total Users: {userCount}
                                        </div>
                                    </div>
                                </div>
                                <div className="row clarity-container">
                                    <div className="col">
                                        <div>
                                            <span onClick={() => this.toggleClarityModal()} className="characteristic-info glyphicon glyphicon-info-sign icon-button" />
                                            <span> Clarity Index</span>
                                        </div>
                                        <div>
                                            <div className="clarity-index clarity-low">&nbsp;</div><span>Slight</span>
                                        </div>
                                        <div>
                                            <div className="clarity-index clarity-mid">&nbsp;</div><span>Moderate</span>
                                        </div>
                                        <div>
                                            <div className="clarity-index clarity-high">&nbsp;</div><span>Clear</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xs-6">
                                {getStylePercentagesPresentation()}<span></span>
                            </div>
                        </div>
                    </div>}
                    <PonderaModal title="Clarity Index" content="" complexContent={clarityContent} show={this.state.clarityModal} simpleTitle={true} onHide={() => this.toggleClarityModal()} />
                </PVAContentBody>
            </div>);
    }

    private getConfigText(ind: number) {
        if (!this.state.config) {
            return <span />;
        }
        switch (ind) {
            case 0:
                if (this.state.config && this.state.config.topLeft && this.state.config.topLeft !== '') {
                    return <span>{this.state.config.topLeft}</span>;
                }
                return <span>INFORMATION GATHERING: Adapting</span>;
            case 1:
                if (this.state.config && this.state.config.topRight && this.state.config.topRight !== '') {
                    return <span>{this.state.config.topRight}</span>;
                }
                return <span>DECISION-MAKING: Organizing</span>;
            case 2:
                if (this.state.config && this.state.config.bottomLeft && this.state.config.bottomLeft !== '') {
                    return <span>{this.state.config.bottomLeft}</span>;
                }
                return <span>INFORMATION GATHERING: Organizing</span>;
            case 3:
                if (this.state.config && this.state.config.bottomRight && this.state.config.bottomRight !== '') {
                    return <span>{this.state.config.bottomRight}</span>;
                }
                return <span>DECISION-MAKING: Adapting</span>;
            default:
                return <span />;
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/teamology/TeamologyReport.tsx